import { useMemo } from 'react';
import {
  Body1Strong,
  Card,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  createTableColumn,
  makeStyles,
  tokens,
  useTableFeatures,
} from '@fluentui/react-components';
import { Warning16Filled } from '@fluentui/react-icons';
import { AppLink, ControlledTooltip } from '@shared/components';
import { usePersistedColumnSizing } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n';
import { useItemTableStyles } from '@modules/items';
import { dateToYMD } from '@shared/utils/date';
import { PAST_VALIDITY_DATE_MESSAGE, REGISTRATIONS_TABLE_ID } from '../constants';
import { toMetadataText } from '../helpers/registrationForm';
import { REGISTRATION_FIELDS } from '../registrationFields.config';
import { ERegistrationWarning, TRegistrationDetails } from '../types';
import RegistrationStatusBadge from './RegistrationStatusBadge';

type TProps = {
  items: TRegistrationDetails[];
};

const TABLE_FIELDS = REGISTRATION_FIELDS.filter(({ showInTable }) => showInTable);
const COLUMN_IDS = [
  'registration',
  'country',
  'products',
  'status',
  'validUntil',
  ...TABLE_FIELDS.map(({ key }) => key),
];

// Mirrors LiteratureTable: the tooltip only appears once the joined names outgrow a typical cell.
const PRODUCTS_TOOLTIP_THRESHOLD = 30;

// autoFitColumns stays false - true would re-run width calculation on every render and overwrite user drag-resize.
const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    registration: { defaultWidth: 260, minWidth: 140 },
    country: { defaultWidth: 160, minWidth: 100 },
    products: { defaultWidth: 240, minWidth: 120 },
    status: { defaultWidth: 150, minWidth: 120 },
    validUntil: { defaultWidth: 110, minWidth: 100 },
    ...Object.fromEntries(TABLE_FIELDS.map(({ key }) => [key, { defaultWidth: 180, minWidth: 120 }])),
  },
};

const useStyles = makeStyles({
  warning: {
    display: 'flex',
    color: tokens.colorStatusWarningForeground1,
  },
});

const RegistrationsTable = ({ items }: TProps) => {
  const tableClasses = useItemTableStyles();
  const columns = useMemo(
    () => COLUMN_IDS.map((columnId) => createTableColumn<TRegistrationDetails>({ columnId })),
    []
  );
  const { columnSizing_unstable, getRows } = useTableFeatures({ columns, items, getRowId: (item) => item._id }, [
    usePersistedColumnSizing(REGISTRATIONS_TABLE_ID, columnSizingParams),
  ]);
  const rows = getRows();

  const headerCell = (columnId: string, label: string) => (
    <TableHeaderCell key={columnId} {...columnSizing_unstable.getTableHeaderCellProps(columnId)}>
      <Body1Strong>{label}</Body1Strong>
    </TableHeaderCell>
  );
  const textCell = (columnId: string, text: string) => (
    <TableCell key={columnId} {...columnSizing_unstable.getTableCellProps(columnId)}>
      <TableCellLayout truncate>{text || '–'}</TableCellLayout>
    </TableCell>
  );

  const classes = useStyles();
  return (
    <Card className={tableClasses.card}>
      <Table className={tableClasses.table} {...columnSizing_unstable.getTableProps()}>
        <TableHeader>
          <TableRow>
            {headerCell('registration', TERMINOLOGY.registrationSingle.upper)}
            {headerCell('country', TERMINOLOGY.registrationCountry.upper)}
            {headerCell('products', 'Products')}
            {headerCell('status', 'Status')}
            {headerCell('validUntil', 'Valid until')}
            {TABLE_FIELDS.map(({ key, label }) => headerCell(key, label))}
          </TableRow>
        </TableHeader>
        <TableBody className={tableClasses.tableBody}>
          {rows.map(({ item }) => {
            const products = item.products.map(({ name }) => name).join(', ');
            return (
              <TableRow key={item._id}>
                <TableCell {...columnSizing_unstable.getTableCellProps('registration')}>
                  <AppLink to={`/folder/${item.registrationFolder}`} className="block truncate">
                    {item.folder.name}
                  </AppLink>
                </TableCell>
                {textCell('country', item.country?.name ?? '')}
                <TableCell {...columnSizing_unstable.getTableCellProps('products')}>
                  <ControlledTooltip
                    enable={products.length > PRODUCTS_TOOLTIP_THRESHOLD}
                    content={products}
                    relationship="description"
                    positioning="above-start"
                  >
                    <span className="inline-block max-w-full truncate align-bottom">{products || '–'}</span>
                  </ControlledTooltip>
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('status')}>
                  <div className="flex items-center gap-1">
                    <RegistrationStatusBadge status={item.status} />
                    {item.warnings.includes(ERegistrationWarning.PAST_VALIDITY_DATE) && (
                      <Tooltip content={PAST_VALIDITY_DATE_MESSAGE} relationship="description" withArrow>
                        <span className={classes.warning}>
                          <Warning16Filled />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                {textCell('validUntil', dateToYMD(item.validUntilDate))}
                {TABLE_FIELDS.map(({ key }) => textCell(key, toMetadataText(item.metadata[key])))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
};

export default RegistrationsTable;
